// InviteModal.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { formatBaseLockedInviteCopy } from 'utilities/baseAccessCopy';
import {
    notifyBaseLimitError,
    openBasePlanSettings,
} from 'utilities/baseLimitFeedback';

// Services
import { getUserWithEmail } from 'services/users/UserService';
import {
    createBaseInvites,
    listBaseInvites,
    revokeBaseInvite,
} from 'services/bases/BaseInviteService';
import { subscribeToBaseMembers } from 'services/bases/BaseMemberService';
import { subscribeToBaseCustomRoles } from 'services/bases/BaseCustomRoleService';
import { formatRoleLabel } from 'services/bases/BaseRoleService';
import { isBuiltinRole } from 'security/rbac';

// Hooks
import useBaseAccess from 'hooks/useBaseAccess';

// CSS
import './InviteModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

const ROLE_OWNER = 'owner';
const ROLE_ADMIN = 'admin';
const ROLE_MEMBER = 'member';
const MAX_INVITES_PER_REQUEST = 25;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Normalize an invite role selection. Preserves custom role keys.
 */
function normalizeInviteRole(value) {
    const role = String(value || '').trim();
    if (!role) return ROLE_MEMBER;
    const lowered = role.toLowerCase();
    if (lowered === ROLE_OWNER) return ROLE_MEMBER;
    if (lowered === ROLE_ADMIN || lowered === ROLE_MEMBER) return lowered;
    if (isBuiltinRole(role)) return ROLE_MEMBER;
    return role;
}

/**
 * Normalizes and validates an invite email value.
 *
 * @param {string} value - The raw input value.
 * 
 * @returns {string} Lowercased email when valid, otherwise empty string.
 */
function normalizeInviteEmail(value) {
    const email = String(value || '').trim().toLowerCase();
    if (!email) return '';
    return EMAIL_PATTERN.test(email) ? email : '';
}

/**
 * Splits the input string into individual invite emails based on commas, trimming whitespace 
 * and filtering out empty entries.
 * 
 * @param {string} value - The raw input string containing one or more invite emails.
 * 
 * @returns {string[]} An array of cleaned invite email strings.
 */
function splitTargets(value) {
    return String(value || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
}

/**
 * InviteModal
 *
 * Renders the space invite modal with invite creation and pending invite management.
 *
 * @param {boolean} isOpen - Whether the modal is visible.
 * @param {function} setOpen - Function to set modal visibility.
 * @param {object} base - Active base record.
 * @param {function} onClose - Optional callback fired when modal closes.
 *
 * @returns {ReactNode} The rendered component.
 */
const InviteModal = React.memo(({
    isOpen,
    setOpen,
    base,
    onClose,
}) => {

    // ----- HOOKS ----- //

    // Base Access Context

    const { baseTier, can } = useBaseAccess(base);

    // ----- STATE ----- //

    // Current space members.

    const [members, setMembers] = useState([]);

    // Base key that the loaded members snapshot belongs to.
    const [membersBaseKey, setMembersBaseKey] = useState(null);

    // Custom member-tier roles for invite assignment.
    const [customRoles, setCustomRoles] = useState([]);

    // Invite target input.
    const [inviteInput, setInviteInput] = useState('');

    // Selected role for invite payloads.
    const [inviteRole, setInviteRole] = useState(ROLE_MEMBER);

    // Optional invite message.
    const [inviteMessage, setInviteMessage] = useState('');

    // Invite action in-flight state.
    const [inviteBusy, setInviteBusy] = useState(false);

    // Error feedback text.
    const [errorMessage, setErrorMessage] = useState('');

    // Success/status feedback text.
    const [statusMessage, setStatusMessage] = useState('');

    // Pending space invites list.
    const [pendingInvites, setPendingInvites] = useState([]);

    // Access flags for invite controls.
    const canInvite = can('invite.create');
    const canListInvites = can('invite.list');
    const canRevokeInvites = can('invite.revoke');

    // ----- DEVICE DETECTION ----- //

    // Mobile breakpoint detection for modal sizing.

    const isMobile = useIsMobile();

    // ----- MEMOS ----- //

    // Pending invites loader for modal actions.

    const loadPendingInvites = useCallback(async () => {
        if (!base?.key || !canInvite || !canListInvites) {
            setPendingInvites([]);
            return;
        }

        try {
            const rows = await listBaseInvites({ baseKey: base.key });
            setPendingInvites(Array.isArray(rows) ? rows : []);
        } catch (error) {
            setPendingInvites([]);
            setErrorMessage(error?.message || 'Failed to load pending invites.');
        }
    }, [base?.key, canInvite, canListInvites]);

    // ----- EFFECTS ----- //

    // Subscribe to current space members while the modal is open.

    useEffect(() => {
        if (!isOpen || !base?.key) {
            setMembers([]);
            setMembersBaseKey(null);
            return;
        }

        const baseKey = base.key;
        setMembers([]);
        setMembersBaseKey(null);

        const unsubscribe = subscribeToBaseMembers(
            baseKey,
            (rows) => {
                setMembers(Array.isArray(rows) ? rows : []);
                setMembersBaseKey(baseKey);
            },
            (error) => {
                setMembers([]);
                setMembersBaseKey(null);
                setErrorMessage(error?.message || 'Failed to load members.');
            }
        );

        return () => {
            unsubscribe && unsubscribe();
        };
    }, [isOpen, base?.key]);

    useEffect(() => {
        if (!isOpen || !base?.key) {
            setCustomRoles([]);
            return undefined;
        }

        return subscribeToBaseCustomRoles(
            base.key,
            (rows) => setCustomRoles(Array.isArray(rows) ? rows : []),
            () => setCustomRoles([])
        );
    }, [isOpen, base?.key]);

    // Load pending invites when modal and access state allow it.
    useEffect(() => {
        if (!isOpen || !base?.key || !canInvite) {
            setPendingInvites([]);
            return;
        }

        loadPendingInvites();
    }, [isOpen, base?.key, canInvite, loadPendingInvites]);

    // ----- HANDLERS ----- //

    /**
     * Resets modal invite state and closes the modal.
     */

    const handleClose = () => {
        setInviteBusy(false);
        setErrorMessage('');
        setStatusMessage('');
        setInviteInput('');
        setInviteRole(ROLE_MEMBER);
        setInviteMessage('');
        if (setOpen) setOpen(false);
        if (onClose) onClose();
    };

    /**
     * Builds and sends space invites for entered targets.
     */
    const handleInvite = async () => {
        if (!base?.key || !canInvite || inviteBusy) return;

        const targets = splitTargets(inviteInput);
        if (!targets.length) {
            setErrorMessage('Enter at least one email address.');
            return;
        }

        if (targets.length > MAX_INVITES_PER_REQUEST) {
            setErrorMessage(`You can invite up to ${MAX_INVITES_PER_REQUEST} people at once.`);
            return;
        }

        setInviteBusy(true);
        setErrorMessage('');
        setStatusMessage('');

        try {
            const activeBaseKey = String(base?.key || '').trim();
            const baseMembers = membersBaseKey === activeBaseKey
                ? members.filter((member) => {
                    const memberBaseKey = String(member?.baseKey || '').trim();
                    return !memberBaseKey || memberBaseKey === activeBaseKey;
                })
                : [];

            const existingKeys = new Set(
                baseMembers
                    .map((member) => member.userKey || member.key)
                    .filter(Boolean)
            );
            const existingEmails = new Set(
                baseMembers
                    .map((member) => normalizeInviteEmail(member.email || member.user?.email))
                    .filter(Boolean)
            );
            const roleToAssign = baseTier === ROLE_OWNER
                ? normalizeInviteRole(inviteRole)
                : (
                    normalizeInviteRole(inviteRole) === ROLE_ADMIN
                        ? ROLE_MEMBER
                        : normalizeInviteRole(inviteRole)
                );

            let prepared = 0;
            const invalidEmails = [];
            const skipped = [];
            const invites = [];
            const seenTargets = new Set();

            const appendInvite = (payload, dedupeKey, rawTarget) => {
                if (seenTargets.has(dedupeKey)) {
                    skipped.push(rawTarget);
                    return;
                }
                seenTargets.add(dedupeKey);
                invites.push({
                    ...payload,
                    role: roleToAssign,
                    message: inviteMessage,
                });
                prepared += 1;
            };

            for (const rawTarget of targets) {
                const email = normalizeInviteEmail(rawTarget);
                if (!email) {
                    invalidEmails.push(rawTarget);
                    continue;
                }

                const existingUser = await getUserWithEmail(email);

                if (existingEmails.has(email) || (existingUser?.key && existingKeys.has(existingUser.key))) {
                    skipped.push(rawTarget);
                    continue;
                }

                appendInvite(
                    { email, userKey: existingUser?.key || null },
                    existingUser?.key ? `u:${existingUser.key}` : `e:${email}`,
                    rawTarget
                );

                if (existingUser?.key) {
                    existingKeys.add(existingUser.key);
                }
            }

            if (!invites.length && invalidEmails.length && !skipped.length) {
                setErrorMessage('Enter at least one valid email address.');
                return;
            }

            let createdCount = 0;
            let serverSkippedCount = 0;
            let deliverySentCount = 0;
            let deliveryFailedCount = 0;

            if (invites.length) {
                const response = await createBaseInvites({
                    baseKey: base.key,
                    invites,
                    role: roleToAssign,
                    message: inviteMessage,
                });

                createdCount = Number(response?.createdCount || 0);
                serverSkippedCount = Number(response?.skippedCount || 0);
                deliverySentCount = Number(response?.deliverySentCount || 0);
                deliveryFailedCount = Number(response?.deliveryFailedCount || 0);
                await loadPendingInvites();
            }

            if (!createdCount && !invalidEmails.length && (skipped.length || serverSkippedCount)) {
                setStatusMessage('All selected emails are already members of this space.');
                return;
            }

            const messageParts = [];
            if (deliverySentCount) messageParts.push(`Sent ${deliverySentCount} invite email${deliverySentCount === 1 ? '' : 's'}.`);
            if (createdCount && !deliverySentCount) messageParts.push(`Created ${createdCount} invite${createdCount === 1 ? '' : 's'}.`);
            if (deliveryFailedCount) messageParts.push(`${deliveryFailedCount} email${deliveryFailedCount === 1 ? '' : 's'} failed delivery.`);
            if (prepared && !createdCount && !serverSkippedCount) messageParts.push(`Prepared ${prepared} invite target${prepared === 1 ? '' : 's'}.`);
            if (skipped.length) messageParts.push(`${skipped.length} already existed.`);
            if (serverSkippedCount) messageParts.push(`${serverSkippedCount} skipped on server.`);
            if (invalidEmails.length) messageParts.push(`${invalidEmails.length} invalid email${invalidEmails.length === 1 ? '' : 's'}.`);

            setStatusMessage(messageParts.join(' '));
            if (createdCount > 0) {
                setInviteInput('');
                setInviteRole(ROLE_MEMBER);
                setInviteMessage('');
            }
        } catch (error) {
            const errorCode = String(error?.code || '').trim();
            const errorMessage = String(error?.message || '').trim();
            const isTrialExpired = errorMessage === 'trial_expired'
                && errorCode === 'functions/failed-precondition';

            if (isTrialExpired) {
                openBasePlanSettings();
                setErrorMessage(formatBaseLockedInviteCopy());
                return;
            }

            notifyBaseLimitError({
                error,
                baseKey: base?.key,
            });
            setErrorMessage(errorMessage || 'Failed to send invite.');
        } finally {
            setInviteBusy(false);
        }
    };

    /**
     * Revokes a pending space invite.
     *
     * @param {string} inviteKey - Invite key to revoke.
     */
    const handleRevokeInvite = async (inviteKey) => {
        if (!base?.key || !inviteKey || inviteBusy || !canRevokeInvites) return;

        setInviteBusy(true);
        setErrorMessage('');

        try {
            await revokeBaseInvite({ baseKey: base.key, inviteKey });
            setStatusMessage('Invite revoked.');
            await loadPendingInvites();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to revoke invite.');
        } finally {
            setInviteBusy(false);
        }
    };

    // ----- RENDER ----- //

    return (

        <Modal
          isOpen={isOpen}
          height="auto"
          width={isMobile ? '100%' : '520px'}
          iconKey="person_add"
          title="Invite Members"
          onClose={handleClose}
        >
            <ModalForm>

                {/* SECTION */}
                <div className="invite-modal-section">

                    <ModalLabel label="Email Addresses" />
                    <TextInput
                      disabled={!canInvite || inviteBusy}
                      height="40px"
                      value={inviteInput}
                      multiline
                      expandable
                      placeholder="name@company.com, teammate@company.com"
                      onChange={(event) => setInviteInput(event.target.value)}
                      onEnterPress={handleInvite}
                    />

                    {/* HINT */}
                    <div
                      className="invite-modal-hint"
                    >
                        Separate multiple entries with commas. Max {MAX_INVITES_PER_REQUEST} invites per send.
                    </div>
                </div>

                {/* SECTION */}
                <div className="invite-modal-section invite-modal-role-row">

                    {/* ROLE LEFT */}
                    <div className="invite-modal-role-left">

                        <ModalLabel label="Initial Role" />

                        {/* ROLE COPY */}
                        <div
                          className="invite-modal-role-copy"
                        >
                            {baseTier === ROLE_OWNER
                                ? 'Choose member, a custom role, or admin.'
                                : 'Choose member or a custom role. Only owners can invite admins.'}
                        </div>
                    </div>

                    <SelectInput
                      style={{
                            height: 40,
                            minWidth: 124,
                        }}
                      value={inviteRole}
                      disabled={inviteBusy}
                      options={[
                        { value: ROLE_MEMBER, label: 'member' },
                        ...(baseTier === ROLE_OWNER
                          ? [{ value: ROLE_ADMIN, label: 'admin' }]
                          : []),
                        ...customRoles.map((role) => ({
                          value: role.key,
                          label: role.name || role.key,
                        })),
                      ]}
                      onChange={(event) => setInviteRole(normalizeInviteRole(event.target.value))}
                    />
                </div>

                {/* SECTION */}
                <div className="invite-modal-section">

                    <ModalLabel label=" Message (optional)" />
                    <TextInput
                      style={{
                            maxHeight: 120,
                            minHeight: 84,
                        }}
                      multiline
                      expandable
                      maxLength={280}
                      value={inviteMessage}
                      placeholder="Add a note to your invite"
                      disabled={!canInvite || inviteBusy}
                      onChange={(event) => setInviteMessage(event.target.value)}
                    />
                </div>

                {/* NOTE */}
                {!canInvite && (
                    <div
                      className="invite-modal-note"
                    >
                        Only space owners and admins can invite members.
                    </div>
                )}

                {errorMessage &&
                    <ModalHint hint={errorMessage} />
                }
                {statusMessage && !errorMessage &&
                    <ModalHint hint={statusMessage} />
                }

                {pendingInvites.length > 0 && (

                    <div className="invite-modal-section">

                        {/* LABEL */}
                        <div
                          className="invite-modal-label"
                        >
                            Pending invites
                        </div>

                        {/* MEMBERS LIST */}
                        <div className="invite-modal-members-list">
                            {pendingInvites.map((invite) => {
                                const key = invite.key;
                                const target = invite.invitedEmail || invite.invitedUserKey || key;

                                return (
                                    <div
                                      className="invite-modal-member-row"
                                      key={key}
                                    >

                                        {/* MEMBER META */}
                                        <div className="invite-modal-member-meta">

                                            {/* MEMBER NAME */}
                                            <div className="invite-modal-member-name">{target}</div>

                                            {/* MEMBER SUBTEXT */}
                                            <div className="invite-modal-member-sub">
                                                {formatRoleLabel(invite.role, Object.fromEntries(
                                                    customRoles.map((role) => [role.key, role])
                                                ))} • {invite.deliveryStatus || 'queued'}
                                            </div>

                                            {/* MEMBER SUB ERROR */}
                                            {invite.deliveryStatus === 'failed' && invite.deliveryLastError && (

                                                <div className="invite-modal-member-sub invite-modal-member-sub-error">
                                                    {invite.deliveryLastError}
                                                </div>
                                            )}
                                        </div>

                                        {/* MEMBER ACTIONS */}
                                        <div className="invite-modal-member-actions">
                                            <Button
                                              label="Revoke"
                                              enabled={!inviteBusy && canRevokeInvites}
                                              onClick={() => handleRevokeInvite(key)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

            </ModalForm>

            <ModalButtons>
                <Button
                  label="Close"
                  enabled={!inviteBusy}
                  onClick={handleClose}
                />
                <Button
                  iconKey="send"
                  label={inviteBusy ? 'Sending...' : 'Send invite'}
                  enabled={canInvite && !inviteBusy}
                  onClick={handleInvite}
                />
            </ModalButtons>

        </Modal>
    );

});

// Dev Tools
InviteModal.displayName = 'InviteModal';

export default InviteModal;
